import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { App } from '@capacitor/app';
import { Platform } from '@ionic/angular';
import { PluginListenerHandle } from '@capacitor/core';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root'
})
export class AppStateChangeService implements OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly platform = inject(Platform);
  private readonly platformService = inject(PlatformService);

  private appStateChangeSubject: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public appStateChange$: Observable<boolean> = this.appStateChangeSubject.asObservable();
  private appResumeSubject: Subject<true> = new Subject();
  public appResume$: Observable<true> = this.appResumeSubject.asObservable();
  private appStateChangeListener: PluginListenerHandle;
  private sb: SubscriptionsBag = new SubscriptionsBag();

  constructor() {
    void this.subscribeToAppStateEvents();
  }

  public get appIsActive(): boolean {
    return this.appStateChangeSubject.value;
  }

  get userIsActive(): boolean {
    if (this.platformService.isSSR) return true;

    const userActivation: { isActive: boolean, hasBeenActive: boolean } = (navigator as any)?.userActivation;

    if (!userActivation) return true;

    return userActivation.isActive;
  }

  async ngOnDestroy(): Promise<void> {
    if (this.appStateChangeListener) {
      await this.appStateChangeListener.remove();
    }
    this.sb.unsubscribeAll();
  }

  private async subscribeToAppStateEvents(): Promise<void> {
    // Срабатывает только при возобновлении приложения из фона.
    // Специфичен для платформы Cordova.
    this.sb.sub = this.platform.resume.subscribe(async (): Promise<void> => {
      await this.ngZone.run(async (): Promise<void> => {
        // console.log('\x1b[95m' + 'App resume is: ' + true + '\x1b[0m');
        this.appResumeSubject.next(true);
      });
    });

    // Работает с любыми изменениями состояния приложения (активное или неактивное)
    // Является более универсальным и поддерживает платформы iOS и Android через Capacitor.

    // Variant 1 - addListener
    if (typeof document !== 'undefined') {
      this.appStateChangeListener = await App.addListener('appStateChange', async ({isActive}): Promise<void> => {
        await this.ngZone.run(async (): Promise<void> => {
          // console.log('\x1b[95m' + 'App state is active: ' + isActive + '\x1b[0m');
          this.appStateChangeSubject.next(isActive);
        });
      });
    }
    // Variant 2 - Observable
    // const appStateChangeEvent: Observable<{ isActive: boolean }> = fromEventPattern(
    //   async (handler: NodeEventHandler): Promise<PluginListenerHandle> => await App.addListener('appStateChange', handler),
    //   async (handler: any): Promise<void> => await handler.remove(),
    // );
    //
    // this.sb.sub = appStateChangeEvent.subscribe(async ({isActive}): Promise<void> => {
    //   await this.ngZone.run(async (): Promise<void> => {
    //     console.log('\x1b[95m' + 'App state is active: ' + isActive + '\x1b[0m');
    //     this.appStateChangeSubject.next(isActive);
    //   });
    // });
  }
}
